// CarbonPulse.js
import * as XLSX from "xlsx";

/* ---------- helpers ---------- */
const parseLocalizedNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v;

  let str = String(v).trim();

  // Entferne alles außer Ziffern, Punkt, Komma, Minus
  str = str.replace(/[^0-9,.\-]/g, "");

  // Wenn sowohl Punkt als auch Komma vorkommen → Komma ist Dezimalzeichen
  if (str.includes(".") && str.includes(",")) {
    str = str.replace(/\./g, "").replace(",", ".");
  }
  // Wenn nur Komma vorkommt und genau drei Ziffern danach → Komma ist Tausendertrennung
  else if (/,(\d{3})$/.test(str)) {
    str = str.replace(/,/g, "");
  }
  // Wenn nur Komma vorkommt → Komma ist Dezimalzeichen
  else if (str.includes(",")) {
    str = str.replace(",", ".");
  }
  // Sonst: Punkt als Dezimal oder nichts

  const n = Number(str);
  return Number.isFinite(n) ? n : null;
};

const toNum = parseLocalizedNumber;

const norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const a1 = (colIndex0) => {
  let n = colIndex0 + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/* ===== feste Layout-Konfiguration ===== */
const LAYOUT = {
  FIRST_MONTH_COL_INDEX_0: 3, // D (Excel)
  MONTH_HEADER_ROW_1_BASED: 10, // null = auto

  SECTIONS: {
    elektrizitaet: {
      anchorRow1Based: 10,
      gesamtRowOffsets: [13, 23, 32, 41, 50],
      keysInOrder: [
        "strom_kwh_total",
        "solar_sell_kwh_total",
        "solar_self_kwh_total",
        "bhkw_sell_kwh_total",
        "bhkw_self_kwh_total",
      ],
    },
    bhkw: {
      anchorRow1Based: 61,
      gesamtRowOffsets: [8],
      keysInOrder: ["bhkw_brennstoff_kwh_total"],
    },
    heizung: {
      anchorRow1Based: 70,
      gesamtRowOffsets: [13, 22],
      keysInOrder: ["heizung_gas_kwh_total", "heizung_oel_kwh_total"],
    },
    produktion: {
      anchorRow1Based: 93,
      gesamtRowOffsets: [9, 19, 29, 39],
      keysInOrder: [
        "produktion_stunden_kombi_total",
        "produktion_stunden_lack_total",
        "produktion_stunden_karos_total",
        "anzahl_reparaturen_total",
      ],
    },
    rohstoffe: {
      anchorRow1Based: 133,
      gesamtRowOffsets: [8],
      keysInOrder: ["lack_liter_total"],
    },
  },
};

/* ---------- internals ---------- */
function findRowIndexByTextInColA(rows, text) {
  const needle = norm(text);
  for (let r = 0; r < rows.length; r++) {
    const colA = rows[r]?.[0];
    if (!colA) continue;
    if (norm(colA) === needle) return r;
  }
  for (let r = 0; r < rows.length; r++) {
    const colA = rows[r]?.[0];
    if (!colA) continue;
    if (norm(colA).includes(needle)) return r;
  }
  return -1;
}

function buildColYearMapFromHeaderRow(headerRow, startCol0) {
  const colYear = {};
  for (let c = startCol0; c < headerRow.length; c++) {
    const cell = headerRow[c];

    if (typeof cell === "string") {
      const m = cell.match(/(20\d{2}|\d{2})\s*$/);
      if (m) {
        let y = parseInt(m[1], 10);
        if (y < 100) y += 2000;
        colYear[c] = y;
        continue;
      }
      const d = new Date(cell);
      if (!isNaN(d.getTime())) {
        colYear[c] = d.getFullYear();
        continue;
      }
    }

    if (typeof cell === "number") {
      const dc = XLSX.SSF.parse_date_code(cell);
      if (dc && dc.y) {
        colYear[c] = dc.y;
        continue;
      }
    }

    if (cell instanceof Date && !isNaN(cell.getTime())) {
      colYear[c] = cell.getFullYear();
      continue;
    }
  }
  return colYear;
}

function sumRowByYear(row, startCol0, colYearMap) {
  const out = {};
  for (let c = startCol0; c < row.length; c++) {
    const y = colYearMap[c];
    if (!y) continue;
    const v = toNum(row[c]);
    if (v == null) continue;
    out[y] = (out[y] || 0) + v;
  }
  return out;
}

function mergeYearSums(target, add) {
  for (const [yStr, val] of Object.entries(add)) {
    const y = Number(yStr);
    target[y] = (target[y] || 0) + val;
  }
}

/* ---------- main ---------- */
export const CarbonPulse = {
  type: "CarbonPulse",

  detect(workbook, file) {
    const name = norm(file?.name || "");
    if (name.includes("carbon pulse") || name.includes("carbonpulse")) return true;
    return (workbook?.SheetNames || []).some((s) => norm(s).includes("check"));
  },

  parse(workbook) {
    const sheetName =
      workbook.Sheets["Check-Liste"]
        ? "Check-Liste"
        : workbook.SheetNames.find((s) => norm(s).includes("check")) ||
          workbook.SheetNames[0];
    const ws = workbook.Sheets[sheetName];
    if (!ws) throw new Error(`Sheet '${sheetName}' nicht gefunden.`);

    const rows = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: true, // wichtig: damit Zeilenindex real bleibt
      raw: false,
    });
    if (!Array.isArray(rows) || rows.length === 0) throw new Error("Leeres Sheet.");

    // Month header
    let headerRowIndex;
    if (LAYOUT.MONTH_HEADER_ROW_1_BASED != null) {
      headerRowIndex = Math.max(0, LAYOUT.MONTH_HEADER_ROW_1_BASED - 1);
    } else {
      const MONTH_RE = /(jan|feb|mar|mrz|mär|apr|mai|may|jun|jul|aug|sep|okt|oct|nov|dez|dec)\s*\d{2,4}/i;
      headerRowIndex = rows.findIndex((r) =>
        r?.some?.((c) => typeof c === "string" && MONTH_RE.test(c))
      );
      if (headerRowIndex === -1) headerRowIndex = 10 - 1;
    }
    const headerRow = rows[headerRowIndex] || [];
    const monthStartCol0 = LAYOUT.FIRST_MONTH_COL_INDEX_0;
    const colYear = buildColYearMapFromHeaderRow(headerRow, monthStartCol0);

    // Aggregate
    const jahreswerte = {};
    const debug = [];

    for (const [sectionId, cfg] of Object.entries(LAYOUT.SECTIONS)) {
      let anchorRow0 = -1;

      if (cfg.anchorRow1Based != null) {
        anchorRow0 = cfg.anchorRow1Based - 1;
      } else if (cfg.anchorText) {
        anchorRow0 = findRowIndexByTextInColA(rows, cfg.anchorText);
      }

      if (anchorRow0 === -1) {
        debug.push({ section: sectionId, error: "Kein gültiger Anchor gefunden" });
        continue;
      }

      cfg.gesamtRowOffsets.forEach((offset, i) => {
        const key = cfg.keysInOrder[i];
        const row0 = anchorRow0 + offset;
        const row = rows[row0] || [];
        const perYear = sumRowByYear(row, monthStartCol0, colYear);

        // Debug-Ausgabe
        const firstCol = a1(monthStartCol0);
        const lastCol = a1(monthStartCol0 + 35);
        console.log(
          `%c[DEBUG] ${sectionId} – ${key || "(no key)"}: Zeile ${row0 + 1} (${firstCol}:${lastCol})`,
          "color:#0a0;font-weight:bold",
          "\nWerte:", row.slice(monthStartCol0, monthStartCol0 + 35),
          "\nSummen pro Jahr:", perYear
        );

        if (key) {
          if (!jahreswerte[key]) jahreswerte[key] = {};
          mergeYearSums(jahreswerte[key], perYear);
        }

        debug.push({
          section: sectionId,
          anchor_row_excel: anchorRow0 + 1,
          offset,
          gesamt_row_excel: row0 + 1,
          key: key || "(no key)",
          first_month_col: `${a1(monthStartCol0)} (${monthStartCol0})`,
          peek_first_6_months: row.slice(monthStartCol0, monthStartCol0 + 35),
          per_year: perYear,
        });
      });
    }

    console.groupCollapsed("%cCarbonPulse fixed mapping report", "color:#0b6;font-weight:600");
    console.log("Sheet:", sheetName);
    console.log("Month header row (Excel):", headerRowIndex + 1);
    console.log("First month column:", `${a1(monthStartCol0)} (index0=${monthStartCol0})`);
    console.log("col→year map:", colYear);
    console.table(debug);
    console.groupEnd();

    const availableYears = [...new Set(Object.values(colYear))]
      .filter(Boolean)
      .sort((a, b) => a - b);
    const referenzjahr = availableYears.length ? availableYears.at(-1) : null;

    const strom_kwh_ref = jahreswerte["strom_kwh_total"]?.[referenzjahr] ?? null;
    const heiz_gas_ref = jahreswerte["heizung_gas_kwh_total"]?.[referenzjahr] ?? 0;
    const heiz_oel_ref = jahreswerte["heizung_oel_kwh_total"]?.[referenzjahr] ?? 0;

    return {
      referenzjahr,
      availableYears,
      jahreswerte,

      emissionen: {
        strom: { wert_tonnen: null, scope: 2 },
        heizung: { wert_tonnen: null, scope: 1 },
        fuhrpark: { wert_tonnen: null, scope: 1 },
      },
      verbrauch: {
        strom_kwh_m2: strom_kwh_ref,
        heizung_kwh_m2: (heiz_gas_ref + heiz_oel_ref) || null,
        fuhrpark_spez_kwh_m2: null,
        fuhrpark_abs_kwh: null,
      },
    };
  },

  missingDefaults: {
    abfallTonnen: "",
    verbrauchsmaterialLack: "",
    verbrauchsmaterialAllg: "",
    verbrauchsmaterialErsatzteile: "",
  },

  mergeMissing(baseData, missing) {
    const yearKeys = Object.keys(missing || {}).filter((k) => /^\d{4}$/.test(k));
    if (yearKeys.length) {
      const abfallByYear = {};
      const matsByYear = {};
      for (const y of yearKeys) {
        const m = missing[y] || {};
        const entryAbfall = toNum(m.abfallTonnen);
        const entryMats = {
          lack_chf: toNum(m.verbrauchsmaterialLack),
          materialien_chf: toNum(m.verbrauchsmaterialAllg),
          ersatzteile_chf: toNum(m.verbrauchsmaterialErsatzteile),
        };
        if (entryAbfall != null) abfallByYear[y] = entryAbfall;
        if (Object.values(entryMats).some((v) => v != null)) matsByYear[y] = entryMats;
      }
      return {
        ...baseData,
        abfallByYear,
        verbrauchsmaterialienByYear: matsByYear,
      };
    }

    return {
      ...baseData,
      abfall: { menge_tonnen: toNum(missing.abfallTonnen) },
      verbrauchsmaterialien: {
        lack_chf: toNum(missing.verbrauchsmaterialLack),
        materialien_chf: toNum(missing.verbrauchsmaterialAllg),
        ersatzteile_chf: toNum(missing.verbrauchsmaterialErsatzteile),
      },
    };
  },

  buildCSV({ userId, company, data, year }) {
    const rows = [["Kategorie", "Wert", "Jahr", "User", "Firma"]];
    for (const [key, values] of Object.entries(data || {})) {
      if (values && typeof values === "object" && !Array.isArray(values)) {
        rows.push([key, values[year] ?? "", year, userId ?? "", company ?? ""]);
      } else {
        rows.push([key, values ?? "", year, userId ?? "", company ?? ""]);
      }
    }
    return rows.map((r) => r.join(";")).join("\n");
  },
};
